import React, { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import RoutePlanner from "./RoutePlanner";
import { AuthContext } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/dateFormatter";

// Privacy Notice Component
const MileagePrivacyNotice = ({ onDismiss }) => (
  <div className="alert alert-info alert-dismissible fade show mb-3">
    <button type="button" className="btn-close" onClick={onDismiss}></button>
    <h6 className="alert-heading">
      <i className="bi bi-shield-lock me-2"></i>Protected Health Information
      Notice
    </h6>
    <small>
      This system contains Protected Health Information (PHI). All access is
      tracked for HIPAA compliance.
    </small>
  </div>
);

// HIPAA Footer Component
const HIPAAFooter = () => (
  <div className="text-center text-muted small mt-5 mb-3">
    <hr />
    <p className="mb-2">
      <i className="bi bi-shield-check me-1"></i>
      This system contains confidential information protected under HIPAA.
    </p>
    <p>
      All activities are logged and monitored. Unauthorized access or disclosure
      is prohibited and may result in penalties.
    </p>
  </div>
);

const MileageLogPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [mileageLogs, setMileageLogs] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [editingLogId, setEditingLogId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillData, setPrefillData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    is_paid: "",
    is_billable: "",
    consumer_search: "",
    authorization_search: "",
  });

  const [showPrivacyNotice, setShowPrivacyNotice] = useState(() => {
    // Check if user has dismissed the notice in this session
    return sessionStorage.getItem("mileagePrivacyNoticeDismissed") !== "true";
  });
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenant_id;

  const fetchMileageLogs = async () => {
    try {
      const query = new URLSearchParams({
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.is_paid && { is_paid: filters.is_paid }),
        ...(filters.is_billable && { is_billable: filters.is_billable }),
        ...(filters.consumer_search && {
          consumer_name: filters.consumer_search,
        }),
        ...(filters.authorization_search && {
          authorization_search: filters.authorization_search,
        }),
      }).toString();

      const res = await secureAxios.get(
        `/api/mileage${query ? "?" + query : ""}`
      );

      setMileageLogs(res.data);
    } catch (err) {
      console.error("Error fetching mileage logs:", err);
    }
  };

  useEffect(() => {
    // Fetch logs whenever we switch to the list tab or filters change
    if (tenantId && user?.user_id && activeTab === "list") {
      fetchMileageLogs();
    }
  }, [tenantId, user?.user_id, filters, activeTab]);

  // Load consumers for filter dropdown
  useEffect(() => {
    if (user?.tenant_id) {
      const fetchConsumers = async () => {
        try {
          const res = await secureAxios.get(`/api/consumers`, {
            params: {
              tenant_id: user.tenant_id,
              user_id: user.user_id,
              role: user.role,
              tenant_type: user.tenant_type,
            },
          });
          setConsumers(res.data);
        } catch (err) {
          console.error("Error loading consumers for filter:", err);
        }
      };
      fetchConsumers();
    }
  }, [user]);

  // Load authorizations for filter dropdown
  useEffect(() => {
    if (user?.tenant_id) {
      const fetchAuthorizations = async () => {
        try {
          const res = await secureAxios.get(`/api/auths`, {
            params: {
              tenant_id: user.tenant_id,
              active: true,
            },
          });
          setAuthorizations(res.data);
        } catch (err) {
          console.error("Error loading authorizations for filter:", err);
        }
      };
      fetchAuthorizations();
    }
  }, [user]);

  useEffect(() => {
    const consumerId = searchParams.get("consumer_id");
    const date = searchParams.get("date");
    const userId = searchParams.get("user_id");

    if (consumerId) {
      // Ensure date is in yyyy-MM-dd format (strip time portion if present)
      let formattedDate = new Date().toISOString().split("T")[0];
      if (date) {
        // Handle both "2025-12-30" and "2025-12-30T05:00:00.000Z" formats
        formattedDate = date.includes("T") ? date.split("T")[0] : date;
      }

      setPrefillData({
        consumer_id: consumerId,
        date: formattedDate,
        user_id: userId || null, // Pass user_id (job coach) if provided
      });
      setActiveTab("form");

      // Clear the URL params after reading them
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this mileage log?")) {
      return;
    }

    try {
      await secureAxios.delete(`/api/mileage/${logId}`);
      fetchMileageLogs();
    } catch (err) {
      // Handle error silently or show user-friendly message
    }
  };

  const handleMileageSaved = () => {
    setActiveTab("list");
    setEditingLogId(null); // Clear editing state
    setPrefillData(null);
    fetchMileageLogs(); // Refresh the list
  };

  const handleDismissPrivacyNotice = () => {
    setShowPrivacyNotice(false);
    sessionStorage.setItem("mileagePrivacyNoticeDismissed", "true");
  };

  // Check if any logs contain PHI
  const hasPhiData = mileageLogs.some((log) => log.consumer_name);

  const clearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      is_paid: "",
      is_billable: "",
      consumer_search: "",
      authorization_search: "",
    });
  };

  // Edit handler function
  const handleEdit = (logId) => {
    setEditingLogId(logId);
    setActiveTab("form");
  };

  const handleTabChange = (tabName) => {
    if (tabName === "list") {
      // Clear editing state when going back to list
      setEditingLogId(null);
      setPrefillData(null);
      fetchMileageLogs(); // Refresh the list
    } else if (tabName === "form" && editingLogId) {
      // Keep editingLogId if we're going to edit mode
    } else if (tabName === "form" && !editingLogId) {
      // Clear any residual editing state for new entries
      setEditingLogId(null);
    }
    setActiveTab(tabName);
  };

  return (
    <div className="container-fluid">
      {/* Privacy Notice */}
      {showPrivacyNotice && (
        <MileagePrivacyNotice onDismiss={handleDismissPrivacyNotice} />
      )}

      {/* Custom Tab Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => handleTabChange("list")}
          >
            Mileage Log List
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "form" ? "active" : ""}`}
            onClick={() => handleTabChange("form")}
          >
            {editingLogId ? "Edit Mileage" : "Add Mileage"}
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "list" && (
          <div className="tab-pane active">
            {/* PHI Data Indicator */}
            {hasPhiData && (
              <div className="text-muted small mb-3">
                <i className="bi bi-info-circle me-1"></i>
                Logs marked with <span className="badge bg-info">PHI</span>{" "}
                contain Protected Health Information and all access is logged
                for HIPAA compliance.
              </div>
            )}

            {/* Filter section */}
            <div className="row mb-3">
              <div className="col-md-3">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.start_date}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-3">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.end_date}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-2">
                <label>Paid Status</label>
                <select
                  className="form-control"
                  value={filters.is_paid}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, is_paid: e.target.value }))
                  }
                >
                  <option value="">All</option>
                  <option value="true">Paid</option>
                  <option value="false">Not Paid</option>
                </select>
              </div>
              <div className="col-md-2">
                <label>Billable Status</label>
                <select
                  className="form-control"
                  value={filters.is_billable}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      is_billable: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="true">Billable</option>
                  <option value="false">Non-Billable</option>
                </select>
              </div>

              <div className="col-md-2">
                <label htmlFor="consumer_search">
                  Consumer{" "}
                  <span className="badge bg-info" style={{ fontSize: "0.5em" }}>
                    PHI
                  </span>
                </label>
                <input
                  id="consumer_search"
                  type="text"
                  className="form-control"
                  placeholder="Search consumers..."
                  value={filters.consumer_search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      consumer_search: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="authorization_search">Authorization</label>
                <input
                  id="authorization_search"
                  type="text"
                  className="form-control"
                  placeholder="Search auth #..."
                  value={filters.authorization_search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      authorization_search: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="col-md-2 d-flex align-items-end gap-2">
                <button
                  className="btn btn-primary flex-fill"
                  onClick={fetchMileageLogs}
                >
                  Search
                </button>
                <button className="btn btn-secondary" onClick={clearFilters}>
                  Clear
                </button>
              </div>
            </div>

            {/* Record Count */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="text-muted">
                Showing <strong>{mileageLogs.length}</strong> record
                {mileageLogs.length !== 1 ? "s" : ""}
                {(filters.start_date ||
                  filters.end_date ||
                  filters.is_paid ||
                  filters.is_billable) && <span> (filtered)</span>}
              </div>
            </div>

            {/* Desktop/Tablet View */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: "110px" }}>Date</th>
                    <th style={{ minWidth: "150px" }}>Consumer</th>
                    <th style={{ width: "120px" }}>Authorization #</th>
                    <th style={{ width: "60px" }}>Miles</th>
                    <th style={{ minWidth: "200px" }}>Reason</th>
                    <th style={{ width: "100px" }}>Type</th>
                    <th style={{ width: "70px" }}>Paid</th>
                    <th style={{ width: "90px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mileageLogs.map((log) => (
                    <tr key={log.log_id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDate(log.trip_date)}
                        {log.consumer_name && (
                          <span
                            className="badge bg-info ms-2"
                            title="Contains Protected Health Information"
                          >
                            PHI
                          </span>
                        )}
                      </td>
                      <td>{log.consumer_name || "-"}</td>
                      <td>{log.authorization_number || "-"}</td>
                      <td className="text-center">{log.total_miles}</td>
                      <td
                        style={{
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {log.reason_for_travel || "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            log.is_billable !== false
                              ? "bg-primary"
                              : "bg-secondary"
                          }`}
                        >
                          {log.is_billable !== false
                            ? "Billable"
                            : "Non-Billable"}
                        </span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {log.is_paid ? (
                          <span className="text-success">
                            <i className="bi bi-check-circle"></i> Yes
                          </span>
                        ) : (
                          <span className="text-danger">
                            <i className="bi bi-x-circle"></i> No
                          </span>
                        )}
                      </td>
                      <td>
                        {!log.is_paid ? (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(log.log_id)}
                              title="Edit mileage log"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(log.log_id)}
                              title="Delete mileage log"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        ) : (
                          <span
                            className="text-muted"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="bi bi-lock"></i> Locked
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {mileageLogs.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No mileage records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="d-block d-md-none">
              {mileageLogs.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center text-muted">
                    No mileage records found.
                  </div>
                </div>
              ) : (
                mileageLogs.map((log) => (
                  <div key={log.log_id} className="card mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong>Date:</strong> {formatDate(log.trip_date)}
                        </div>
                        {log.consumer_name && (
                          <span
                            className="badge bg-info"
                            title="Contains Protected Health Information"
                          >
                            PHI
                          </span>
                        )}
                      </div>
                      <div className="row">
                        <div className="col-6">
                          <strong>Miles:</strong> {log.total_miles}
                        </div>
                        <div className="col-6">
                          <strong>Paid:</strong>{" "}
                          {log.is_paid ? (
                            <span className="text-success">
                              <i className="bi bi-check-circle"></i> Yes
                            </span>
                          ) : (
                            <span className="text-danger">
                              <i className="bi bi-x-circle"></i> No
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>Consumer:</strong>{" "}
                          {log.consumer_name || "-"}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>Auth #:</strong>{" "}
                          {log.authorization_number || "-"}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>Reason:</strong>{" "}
                          {log.reason_for_travel || "-"}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>Type:</strong>{" "}
                          <span
                            className={`badge ${
                              log.is_billable !== false
                                ? "bg-primary"
                                : "bg-secondary"
                            }`}
                          >
                            {log.is_billable !== false
                              ? "Billable"
                              : "Non-Billable"}
                          </span>
                        </div>
                      </div>
                      {!log.is_paid ? (
                        <div className="mt-3 d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(log.log_id)}
                          >
                            <i className="bi bi-pencil"></i> Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(log.log_id)}
                          >
                            <i className="bi bi-trash"></i> Delete
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <span className="text-muted">
                            <i className="bi bi-lock"></i> Locked
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "form" && (
          <div className="tab-pane active">
            <RoutePlanner
              editId={editingLogId}
              onFinish={handleMileageSaved}
              prefillConsumerId={prefillData?.consumer_id}
              prefillDate={prefillData?.date}
              prefillUserId={prefillData?.user_id}
            />
          </div>
        )}
      </div>

      {/* HIPAA Footer */}
      <HIPAAFooter />
    </div>
  );
};

export default MileageLogPage;